import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Filter, RotateCcw, Search, SlidersHorizontal } from "lucide-react";
import { Level } from "../enums/level";
import { CoursesFilter } from "../types/coursesFilter";
import { BaseFilterOptionResponse } from "@/types/apiTypes/baseFilterOptionResponse.ts";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet.tsx";

interface CoursesFilterProps {
  filters: CoursesFilter;
  onFiltersChange: (filters: CoursesFilter) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  isLoading?: boolean;
  availableCategories?: BaseFilterOptionResponse[];
  availableTags?: BaseFilterOptionResponse[];
  maxPrice?: number;
}

export const CoursesFilterComponent: React.FC<CoursesFilterProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onResetFilters,
  isLoading = false,
  availableCategories = [],
  availableTags = [],
  maxPrice = 10000,
}) => {
  const [categorySearch, setCategorySearch] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const updateFilters = (updates: Partial<CoursesFilter>) => {
    onFiltersChange({ ...filters, ...updates });
  };

  const handleCategoryChange = (category: string, checked: boolean) => {
    const newCategories = checked
      ? [...(filters.categories || []), category]
      : (filters.categories || []).filter((c) => c !== category);
    updateFilters({ categories: newCategories });
  };

  const handleTagChange = (tag: string, checked: boolean) => {
    const newTags = checked
      ? [...(filters.tags || []), tag]
      : (filters.tags || []).filter((t) => t !== tag);
    updateFilters({ tags: newTags });
  };

  const handleLevelChange = (level: Level, checked: boolean) => {
    const newLevels = checked
      ? [...(filters.levels || []), level]
      : (filters.levels || []).filter((l) => l !== level);
    updateFilters({ levels: newLevels });
  };

  const handlePriceChange = (values: number[]) => {
    updateFilters({
      minPrice: values[0] === 0 ? null : values[0],
      maxPrice: values[1] === maxPrice ? null : values[1],
    });
  };

  const removeCategory = (category: string) => {
    handleCategoryChange(category, false);
  };

  const removeTag = (tag: string) => {
    handleTagChange(tag, false);
  };

  const removeLevel = (level: Level) => {
    handleLevelChange(level, false);
  };

  const getTagName = (id: string) => {
    return availableTags.find((tag) => tag.id === id)?.name || "";
  };

  const getCategoryName = (id: string) => {
    return (
      availableCategories.find((category) => category.id === id)?.name || ""
    );
  };

  const getLevelName = (level: Level) => {
    switch (level) {
      case Level.Beginner:
        return "Başlangıç";
      case Level.Intermediate:
        return "Orta";
      case Level.Advanced:
        return "Zor";
      default:
        return "Unknown";
    }
  };

  // Filtrelenmiş kategoriler
  const filteredCategories = useMemo(() => {
    return availableCategories.filter((category) =>
      category.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
  }, [availableCategories, categorySearch]);

  // Filtrelenmiş etiketler
  const filteredTags = useMemo(() => {
    return availableTags.filter((tag) =>
      tag.name.toLowerCase().includes(tagSearch.toLowerCase())
    );
  }, [availableTags, tagSearch]);

  const hasActiveFilters =
    (filters.categories?.length || 0) > 0 ||
    (filters.tags?.length || 0) > 0 ||
    (filters.levels?.length || 0) > 0 ||
    filters.search ||
    filters.minPrice !== null ||
    filters.maxPrice !== null;

  const handleApplyAndClose = () => {
    onApplyFilters();
    setIsOpen(false);
  };

  const handleResetAndClose = () => {
    onResetFilters();
    setIsOpen(false);
  };

  const FilterContent = React.useMemo(
    () => (
      <div className="space-y-6">
        {/* Arama */}
        <div className="space-y-2">
          <Label htmlFor="search">Arama</Label>
          <Input
            id="search"
            placeholder="Kurs ara..."
            value={filters.search || ""}
            onChange={(e) => updateFilters({ search: e.target.value })}
            disabled={isLoading}
          />
        </div>

        {/* Kategoriler */}
        <div className="space-y-3">
          <Label>Kategoriler</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Kategori ara..."
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              className="pl-10"
              disabled={isLoading}
            />
          </div>

          <ScrollArea className="h-32 md:h-48 w-full border rounded-md p-2">
            <div className="space-y-2">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`category-${category.id}`}
                      checked={(filters.categories || []).includes(category.id)}
                      onCheckedChange={(checked) =>
                        handleCategoryChange(category.id, checked as boolean)
                      }
                      disabled={isLoading}
                    />
                    <Label
                      htmlFor={`category-${category.id}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {category.name}
                    </Label>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Kategori bulunamadı
                </p>
              )}
            </div>
          </ScrollArea>

          {(filters.categories?.length || 0) > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-gray-600">
                Seçili Kategoriler:
              </Label>
              <div className="flex flex-wrap gap-1">
                {(filters.categories || []).map((category) => (
                  <Badge key={category} className="text-xs">
                    {getCategoryName(category)}
                    <button
                      onClick={() => removeCategory(category)}
                      className="ml-1 hover:text-red-500"
                      disabled={isLoading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Etiketler */}
        <div className="space-y-3">
          <Label>Etiketler</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Etiket ara..."
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              className="pl-10"
              disabled={isLoading}
            />
          </div>

          <ScrollArea className="h-32 md:h-48 w-full border rounded-md p-2">
            <div className="space-y-2">
              {filteredTags.length > 0 ? (
                filteredTags.map((tag) => (
                  <div key={tag.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`tag-${tag.id}`}
                      checked={(filters.tags || []).includes(tag.id)}
                      onCheckedChange={(checked) =>
                        handleTagChange(tag.id, checked as boolean)
                      }
                      disabled={isLoading}
                    />
                    <Label
                      htmlFor={`tag-${tag.id}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {tag.name}
                    </Label>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Etiket bulunamadı
                </p>
              )}
            </div>
          </ScrollArea>

          {(filters.tags?.length || 0) > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-gray-600">Seçili Etiketler:</Label>
              <div className="flex flex-wrap gap-1">
                {(filters.tags || []).map((tag) => (
                  <Badge key={tag} className="text-xs">
                    {getTagName(tag)}
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:text-red-500"
                      disabled={isLoading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Seviye */}
        <div className="space-y-3">
          <Label>Seviye</Label>
          <div className="grid grid-cols-1 gap-2">
            {Object.values(Level)
              .filter((level) => typeof level === "number")
              .map((level) => (
                <div key={level} className="flex items-center space-x-2">
                  <Checkbox
                    id={`level-${level}`}
                    checked={(filters.levels || []).includes(level as Level)}
                    onCheckedChange={(checked) =>
                      handleLevelChange(level as Level, checked as boolean)
                    }
                    disabled={isLoading}
                  />
                  <Label htmlFor={`level-${level}`} className="text-sm flex-1">
                    {getLevelName(level as Level)}
                  </Label>
                </div>
              ))}
          </div>

          {(filters.levels?.length || 0) > 0 && (
            <div className="flex flex-wrap gap-1">
              {(filters.levels || []).map((level) => (
                <Badge key={level} className="text-xs">
                  {getLevelName(level)}
                  <button
                    onClick={() => removeLevel(level)}
                    className="ml-1 hover:text-red-500"
                    disabled={isLoading}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Fiyat Aralığı */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Fiyat Aralığı</Label>
            <span className="text-sm font-medium text-primary">
              ₺{filters.minPrice ?? 0} - ₺{filters.maxPrice ?? maxPrice}
            </span>
          </div>
          <div className="pt-2 pb-1">
            <Slider
              min={0}
              max={maxPrice}
              step={5}
              value={[filters.minPrice ?? 0, filters.maxPrice ?? maxPrice]}
              onValueChange={handlePriceChange}
              className="w-full"
              disabled={isLoading}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>₺0</span>
            <span>₺{maxPrice.toLocaleString("tr-TR")}</span>
          </div>
        </div>
      </div>
    ),
    [
      filters,
      isLoading,
      categorySearch,
      tagSearch,
      filteredCategories,
      filteredTags,
      availableCategories,
      availableTags,
    ]
  );

  return (
    <>
      {/* Mobile Filter Button */}
      <div className="lg:hidden mb-4">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="w-full flex items-center gap-2 bg-transparent"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filtreler
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-auto">
                  {(filters.categories?.length || 0) +
                    (filters.tags?.length || 0) +
                    (filters.levels?.length || 0)}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-full sm:w-80 overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filtreler
                {hasActiveFilters && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleResetAndClose}
                    disabled={isLoading}
                    className="ml-auto"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              {FilterContent}
              <div className="flex gap-2 pt-6 sticky bottom-0 bg-background">
                <Button
                  onClick={handleApplyAndClose}
                  className="flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? "Uygulanıyor..." : "Filtrele"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleResetAndClose}
                  disabled={isLoading}
                >
                  Temizle
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Filter Sidebar */}
      <div className="hidden lg:block">
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtreler
              {hasActiveFilters && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onResetFilters}
                  disabled={isLoading}
                  className="ml-auto"
                >
                  <RotateCcw className="h-4 w-4" />
                  Temizle
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {FilterContent}
            <div className="flex gap-2 pt-6">
              <Button
                onClick={onApplyFilters}
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading ? "Uygulanıyor..." : "Filtrele"}
              </Button>
              <Button
                variant="outline"
                onClick={onResetFilters}
                disabled={isLoading}
              >
                Temizle
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
